import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import {
  LuArrowLeft,
  LuCircleCheck,
  LuFileText,
  LuLoaderPinwheel,
  LuPaperclip,
  LuPlus,
  LuSend,
  LuTrash2,
  LuX,
} from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { getAgentDisplayName } from '../../entities/chat-agent';
import { PROVIDER_AGENT } from '../../entities/provider';
import { StandaloneProjectSummary } from '../../entities/standalone-project';
import { TrelloBoardStats } from '../../entities/trello-board-stats';
import { ChatSession, ChatSessionMessage, createSession, getProjectSessions } from '../../shared/api/chat-sessions';
import { auth } from '../../shared/firebase/firebase';
import { DashboardChatMessage, useDashboardChat } from '../../shared/hooks/use-dashboard-chat';
import { useStandaloneProject } from '../../shared/hooks/use-standalone-project';
import { useStandaloneProjects } from '../../shared/hooks/use-standalone-projects';
import { useTrelloBoards } from '../../shared/hooks/use-trello-boards';
import { useWizardProposal } from '../../shared/hooks/use-wizard-proposal';
import { fmtDays, fmtPct, psiBadge, safeNum } from '../../shared/utils/stat-helpers';
import {
  getVirtualProjectAgent,
  isStandaloneProjectId,
  isStudioProjectId,
  isWizardProjectId,
  resolveApiProjectId,
} from '../../shared/utils/virtual-projects';
import MarkdownRenderer from '../MarkdownRenderer';
import { AgentSwitcher } from './AgentSwitcher';
import { AttachedProject, AttachProjectControl } from './AttachProjectControl';
import ChatMessageActions from './ChatMessageActions';
import WizardProposalPanel from './WizardProposalPanel';

export interface ProjectMetrics {
  psi: number;
  throughput: number;
  avgCycleDays: number;
  reopenRate: number;
}

interface SessionChatViewProps {
  sessionId: string;
  projectId: string;
  projectName?: string;
  history: ChatSessionMessage[];
  metrics: ProjectMetrics | null;
  trelloMetrics?: TrelloBoardStats | null;
  standaloneMetrics?: StandaloneProjectSummary | null;
}

const MetricsStrip: React.FC<{ metrics: ProjectMetrics }> = ({ metrics }) => {
  const psi = safeNum(metrics.psi);
  const badge = psiBadge(psi);
  const reopenHigh = metrics.reopenRate > 15;
  const cycleHigh = metrics.avgCycleDays > 3;

  return (
    <div className="flex items-center gap-3 px-4 py-2 border-b border-base-300 bg-base-200/50 flex-wrap">
      {/* PSI */}
      <div className="flex items-center gap-1.5">
        <span className="text-xs opacity-50">PSI</span>
        <span className="font-semibold text-sm">{psi}</span>
        <span className={`badge badge-xs ${badge.cls}`}>{badge.label}</span>
      </div>

      <div className="w-px h-4 bg-base-300" />

      {/* Throughput */}
      <div className="flex items-center gap-1.5">
        <span className="text-xs opacity-50">Throughput</span>
        <span className="font-semibold text-sm">{metrics.throughput}</span>
      </div>

      <div className="w-px h-4 bg-base-300" />

      {/* Avg cycle time */}
      <div className="flex items-center gap-1.5">
        <span className="text-xs opacity-50">Cycle time</span>
        <span className={`font-semibold text-sm ${cycleHigh ? 'text-warning' : ''}`}>
          {fmtDays(metrics.avgCycleDays)}
        </span>
      </div>

      <div className="w-px h-4 bg-base-300" />

      {/* Reopen rate */}
      <div className="flex items-center gap-1.5">
        <span className="text-xs opacity-50">Reopen</span>
        <span className={`font-semibold text-sm ${reopenHigh ? 'text-error' : ''}`}>{fmtPct(metrics.reopenRate)}</span>
      </div>
    </div>
  );
};

const TrelloMetricsStrip: React.FC<{ metrics: TrelloBoardStats }> = ({ metrics }) => {
  const reopenHigh = (metrics.reopenRatePercent ?? 0) > 15;
  const cycleHigh = (metrics.cycleTimeAvgHours ?? 0) / 24 > 3;

  return (
    <div className="flex items-center gap-3 px-4 py-2 border-b border-base-300 bg-base-200/50 flex-wrap">
      <div className="flex items-center gap-1.5">
        <span className="text-xs opacity-50">Completed</span>
        <span className="font-semibold text-sm">{metrics.completedCount}</span>
      </div>
      <div className="w-px h-4 bg-base-300" />
      <div className="flex items-center gap-1.5">
        <span className="text-xs opacity-50">Cycle time</span>
        <span className={`font-semibold text-sm ${cycleHigh ? 'text-warning' : ''}`}>
          {metrics.cycleTimeAvgHours != null ? fmtDays(metrics.cycleTimeAvgHours / 24) : '—'}
        </span>
      </div>
      <div className="w-px h-4 bg-base-300" />
      <div className="flex items-center gap-1.5">
        <span className="text-xs opacity-50">WIP</span>
        <span className="font-semibold text-sm">{metrics.wipCount}</span>
      </div>
      <div className="w-px h-4 bg-base-300" />
      <div className="flex items-center gap-1.5">
        <span className="text-xs opacity-50">Reopen</span>
        <span className={`font-semibold text-sm ${reopenHigh ? 'text-error' : ''}`}>
          {metrics.reopenRatePercent != null ? fmtPct(metrics.reopenRatePercent) : '—'}
        </span>
      </div>
    </div>
  );
};

const StandaloneMetricsStrip: React.FC<{ metrics: StandaloneProjectSummary }> = ({ metrics }) => (
  <div className="flex items-center gap-3 px-4 py-2 border-b border-base-300 bg-base-200/50 flex-wrap">
    <div className="flex items-center gap-1.5">
      <span className="text-xs opacity-50">To do</span>
      <span className="font-semibold text-sm">{metrics.todoCount}</span>
    </div>
    <div className="w-px h-4 bg-base-300" />
    <div className="flex items-center gap-1.5">
      <span className="text-xs opacity-50">In progress</span>
      <span className="font-semibold text-sm">{metrics.inProgressCount}</span>
    </div>
    <div className="w-px h-4 bg-base-300" />
    <div className="flex items-center gap-1.5">
      <span className="text-xs opacity-50">Done</span>
      <span className="font-semibold text-sm">{metrics.doneCount}</span>
    </div>
    <div className="w-px h-4 bg-base-300" />
    <div className="flex items-center gap-1.5">
      <span className="text-xs opacity-50">Overdue</span>
      <span className={`font-semibold text-sm ${metrics.overdueCount > 0 ? 'text-error' : ''}`}>
        {metrics.overdueCount}
      </span>
    </div>
  </div>
);

const SessionChatView: React.FC<SessionChatViewProps> = ({
  sessionId,
  projectId,
  projectName,
  history,
  metrics,
  trelloMetrics,
  standaloneMetrics,
}) => {
  const navigate = useNavigate();
  const userId = auth.currentUser?.uid ?? '';
  const [input, setInput] = useState('');
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [manualAgent, setManualAgent] = useState<string | null>(null);
  // Studio (general chat) only — a project attached mid-conversation via AttachProjectControl.
  // Local-only: nothing server-side to persist it in, so reopening the session later starts
  // unattached again (see AttachProjectControl's doc comment).
  const [attachedProject, setAttachedProject] = useState<AttachedProject | null>(null);
  const [attachEvents, setAttachEvents] = useState<{ atIndex: number; name: string }[]>([]);

  const { liveMessages, typingText, sendMessage, clearMessages, uploadFile } = useDashboardChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const isWizard = isWizardProjectId(projectId);
  const isStudio = isStudioProjectId(projectId);
  // 'standalone' is the bootstrap sentinel (no real project yet); a session can also be opened
  // directly on a real, already-known StandaloneProject._id (from GET /api/user/projects) —
  // both need the same StandaloneAgent/no-fast-path treatment.
  const isStandaloneSentinel = isStandaloneProjectId(projectId);
  const { boards: trelloBoards } = useTrelloBoards();
  const { projects: standaloneProjects } = useStandaloneProjects();
  const isTrelloBoard = trelloBoards.some((b) => b.id === projectId);
  const knownStandaloneProject = standaloneProjects.find((p) => p._id === projectId);
  const isStandalone = isStandaloneSentinel || !!knownStandaloneProject;
  // A project attached to a studio session needs the same explicit StandaloneAgent pin as a
  // native Standalone session — it has no mcp-context registry for resolveBoundPlatform to find
  // (see simple-coordinator.graph.ts). A Jira/Trello pick resolves on its own from boardId alone.
  const attachedAgent = attachedProject?.provider === 'standalone' ? 'StandaloneAgent' : undefined;
  const connectedAgent =
    getVirtualProjectAgent(projectId) ??
    (isTrelloBoard ? 'TrelloAgent' : knownStandaloneProject ? 'StandaloneAgent' : attachedAgent);

  // A session opened with no prior messages (e.g. just created via "New session") gets the same
  // stale-`built`-snapshot treatment as WizardPanel — see useWizardProposal's `hasHistory` doc.
  const {
    proposal,
    priorBuilt,
    dismissPriorBuilt,
    taskProgress,
    clear: clearWizardProposal,
    retry: retryWizard,
  } = useWizardProposal(isWizard, history.length > 0);
  // Once a Wizard proposal is built, WizardAgent's job is done — default to the platform agent
  // that actually knows how to keep managing the real project, but respect a manual override.
  const builtPlatform = isWizard && proposal?.status === 'built' ? proposal.platform : undefined;
  const effectiveAgent = manualAgent ?? (builtPlatform ? PROVIDER_AGENT[builtPlatform] : connectedAgent);
  // Only the sentinel bootstrap case needs the live "just created" binding — a session opened
  // directly on a known project already has its real id in `projectId`.
  const { project: liveBoundProject } = useStandaloneProject();
  const boundStandaloneId = isStandaloneSentinel ? liveBoundProject?._id : projectId;
  const displayName =
    attachedProject?.name ??
    knownStandaloneProject?.name ??
    (isStandaloneSentinel ? liveBoundProject?.name : undefined) ??
    projectName;

  // Once a real Standalone project exists, new sessions should key on it directly instead of the
  // shared bootstrap bucket; Wizard/Studio never get a real project id, so they always resolve to
  // their own per-user sentinel. See rest-api.md §Chat Sessions.
  const sessionProjectId = isStandaloneSentinel
    ? (liveBoundProject?._id ?? resolveApiProjectId(projectId, userId))
    : isWizard || isStudio
      ? resolveApiProjectId(projectId, userId)
      : projectId;

  const [file, setFile] = useState<File | null>(null);
  const [docId, setDocId] = useState<string>('');
  const [isFileLoading, setIsFileLoading] = useState(false);

  const allMessages: DashboardChatMessage[] = [...history.map((m) => ({ ...m })), ...liveMessages];

  // Interleaves "Attached to X" markers at the point they happened — honest about routing being
  // per-message: earlier answers genuinely weren't scoped to the attached project.
  const timeline = useMemo(() => {
    const items: ({ kind: 'divider'; name: string } | { kind: 'message'; msg: DashboardChatMessage })[] = [];
    allMessages.forEach((msg, i) => {
      attachEvents.filter((e) => e.atIndex === i).forEach((e) => items.push({ kind: 'divider', name: e.name }));
      items.push({ kind: 'message', msg });
    });
    attachEvents
      .filter((e) => e.atIndex === allMessages.length)
      .forEach((e) => items.push({ kind: 'divider', name: e.name }));
    return items;
  }, [allMessages.length, attachEvents]);

  // Load sibling sessions for the session switcher
  useEffect(() => {
    if (!sessionProjectId) return;
    setSessionsLoading(true);
    getProjectSessions(sessionProjectId)
      .then(setSessions)
      .catch(() => {})
      .finally(() => setSessionsLoading(false));
  }, [sessionProjectId]);

  // Clear live messages when sessionId changes (session switch)
  useEffect(() => {
    clearMessages();
    clearWizardProposal();
    setAttachedProject(null);
    setAttachEvents([]);
  }, [sessionId, clearMessages, clearWizardProposal]);

  const handleAttachProject = (project: AttachedProject) => {
    setAttachedProject(project);
    setAttachEvents((prev) => [...prev, { atIndex: allMessages.length, name: project.name }]);
  };

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [allMessages.length, typingText]);

  const handleSend = (text = input) => {
    if (!text.trim()) return;
    sendMessage(text.trim(), {
      sessionId,
      // context.boardId: real Jira/Trello id, the bound Standalone project once known, the
      // wizard-built project once switched off WizardAgent, whatever's been attached to a studio
      // session so far (undefined until then — the coordinator classifies freely, see
      // simple-coordinator.graph.ts), or nothing for an in-progress Wizard session / an unbound
      // Standalone conversation.
      projectId: isWizard
        ? effectiveAgent !== 'WizardAgent'
          ? proposal?.builtProjectId
          : undefined
        : isStandalone
          ? boundStandaloneId
          : isStudio
            ? attachedProject?.id
            : projectId,
      connectedAgents: effectiveAgent ? [effectiveAgent] : undefined,
      docId: docId || undefined,
    });
    setInput('');
    setFile(null);
    setDocId('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';
  };

  const handleRetryWizardBuild = async () => {
    setIsRetrying(true);
    try {
      await retryWizard();
    } catch {
      toast.error('Failed to start retry.');
    } finally {
      setIsRetrying(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setIsFileLoading(true);
    uploadFile(
      selectedFile,
      auth.currentUser?.uid || '',
      sessionId,
      (uploadedDocId) => {
        toast.success('File processed successfully');
        setDocId(uploadedDocId);
        setIsFileLoading(false);
      },
      (error) => {
        toast.error(error);
        setFile(null);
        setIsFileLoading(false);
      },
    );
  };

  const handleRemoveFile = () => {
    setFile(null);
    setDocId('');
  };

  const handleNewSession = async () => {
    setIsCreating(true);
    try {
      const session = await createSession(sessionProjectId);
      setSessions((prev) => [session, ...prev]);
      navigate(`/chat/sessions/${session._id}?projectId=${projectId}`);
    } catch {
      // ignore
    } finally {
      setIsCreating(false);
    }
  };

  const handleSwitchSession = (id: string) => {
    if (id !== sessionId) {
      navigate(`/chat/sessions/${id}?projectId=${projectId}`);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Title row */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-base-300 shrink-0 bg-base-100">
        <div className="flex items-center gap-3 min-w-0">
          <button
            className="btn btn-ghost btn-sm btn-circle"
            onClick={() => navigate(`/chat?projectId=${projectId}`)}
            title="Back to sessions"
          >
            <LuArrowLeft size={16} />
          </button>

          {displayName && <div className="font-semibold truncate max-w-[200px]">{displayName}</div>}

          {!sessionsLoading && sessions.length > 1 && (
            <select
              className="select select-bordered select-sm bg-base-100"
              value={sessionId}
              onChange={(e) => handleSwitchSession(e.target.value)}
            >
              {sessions.map((s) => (
                <option key={s._id} value={s._id}>
                  {s.title ?? new Date(s.createdAt).toLocaleString()}
                </option>
              ))}
            </select>
          )}
          {sessionsLoading && <span className="loading loading-spinner loading-xs opacity-40" />}
        </div>

        <button className="btn btn-outline btn-sm gap-1 shrink-0" onClick={handleNewSession} disabled={isCreating}>
          {isCreating ? <LuLoaderPinwheel className="animate-spin" size={14} /> : <LuPlus size={14} />}
          New session
        </button>
      </div>

      {/* Metrics strip */}
      {metrics && <MetricsStrip metrics={metrics} />}
      {trelloMetrics && <TrelloMetricsStrip metrics={trelloMetrics} />}
      {standaloneMetrics && <StandaloneMetricsStrip metrics={standaloneMetrics} />}

      {/* Agent switcher — offered once the wizard has actually built something */}
      {builtPlatform && (
        <div className="px-4 py-2 border-b border-base-300">
          <AgentSwitcher
            builtPlatform={builtPlatform}
            activeAgent={effectiveAgent ?? 'WizardAgent'}
            onChange={setManualAgent}
          />
        </div>
      )}

      {/* Studio (general chat) — attach a real project to this conversation, or change it later */}
      {isStudio && (
        <div className="px-4 py-2 border-b border-base-300">
          <AttachProjectControl attachedName={attachedProject?.name} onAttach={handleAttachProject} />
        </div>
      )}

      {/* Wizard: previously built project, shown passively so a fresh session isn't blocked */}
      {isWizard && priorBuilt && (
        <div className="mx-4 mt-3 rounded-xl bg-base-200 shadow-card px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0 text-sm">
            <LuCircleCheck className="text-success shrink-0" />
            <span className="truncate">
              Your last wizard project — <b>{priorBuilt.projectName || 'Untitled project'}</b> on{' '}
              <span className="capitalize">{priorBuilt.platform}</span> — is already built. Describe a new spec below to
              start another.
            </span>
          </div>
          <button className="btn btn-ghost btn-xs btn-circle shrink-0" onClick={dismissPriorBuilt} title="Dismiss">
            <LuX size={14} />
          </button>
        </div>
      )}

      {/* Wizard proposal */}
      {isWizard && proposal && (
        <WizardProposalPanel
          proposal={proposal}
          taskProgress={taskProgress}
          onQuickAction={handleSend}
          onRetry={handleRetryWizardBuild}
          isRetrying={isRetrying}
        />
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {allMessages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full opacity-40 gap-2 text-sm text-center px-8">
            <span>
              {isWizard
                ? 'Describe your project spec (text and/or an attached file) to get a methodology and task breakdown proposal.'
                : isStudio && !attachedProject
                  ? "Ask me anything — about a specific project, or to figure out what you're working on. Attach a project above whenever you're ready."
                  : 'No messages yet. Start the conversation.'}
            </span>
          </div>
        )}

        {timeline.map((item, i) =>
          item.kind === 'divider' ? (
            <div key={`divider-${i}`} className="flex items-center gap-2 py-1">
              <div className="flex-1 h-px bg-base-300" />
              <span className="text-[11px] uppercase tracking-wide opacity-40 shrink-0">Attached to {item.name}</span>
              <div className="flex-1 h-px bg-base-300" />
            </div>
          ) : item.msg.isUser ? (
            <div key={`msg-${i}`} className="flex justify-end">
              <div className="bg-primary/10 text-sm rounded-xl rounded-br-sm px-4 py-2 max-w-[75%] whitespace-pre-wrap">
                {item.msg.text}
              </div>
            </div>
          ) : !item.msg.hidden ? (
            <div key={`msg-${i}`} className="text-sm opacity-90 max-w-[85%]">
              <div className="text-xs font-semibold opacity-50 mb-1">{getAgentDisplayName(item.msg.sender)}</div>
              <MarkdownRenderer markdown={item.msg.text} />
              {(item.msg as any).typing && (
                <span className="inline-block w-1.5 h-3.5 bg-current opacity-60 animate-pulse ml-0.5 align-middle" />
              )}
              {!!item.msg.actions?.length && <ChatMessageActions actions={item.msg.actions} />}
            </div>
          ) : null,
        )}

        {typingText && (
          <div className="flex items-center gap-2 text-sm opacity-50">
            <LuLoaderPinwheel className="animate-spin shrink-0" size={14} />
            {typingText}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-base-300 shrink-0 bg-base-100">
        {isWizard && (file || isFileLoading) && (
          <div className="mb-2 inline-flex items-center gap-2 bg-base-200 rounded-lg px-3 py-2">
            {isFileLoading ? (
              <LuLoaderPinwheel className="animate-spin opacity-70" />
            ) : (
              <LuFileText className="opacity-70" />
            )}
            <span className="text-sm truncate max-w-[300px]">{file?.name}</span>
            {!isFileLoading && (
              <button className="btn btn-ghost btn-xs" onClick={handleRemoveFile} title="Remove file">
                <LuTrash2 />
              </button>
            )}
          </div>
        )}

        <div className="flex items-end gap-2">
          {isWizard && (
            <label
              className="btn btn-ghost btn-sm btn-circle"
              title="Attach a spec file (.pdf, .doc, .docx, .md, .txt)"
            >
              <input
                type="file"
                accept=".pdf,.doc,.docx,.md,.markdown,.txt"
                onChange={handleFileUpload}
                className="hidden"
              />
              <LuPaperclip className="text-lg" />
            </label>
          )}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              e.currentTarget.style.height = 'auto';
              e.currentTarget.style.height = `${e.currentTarget.scrollHeight}px`;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder={
              isWizard
                ? 'Describe your spec, or reply to refine the proposal…'
                : isStandalone
                  ? 'Describe what you want to track…'
                  : isStudio && !attachedProject
                    ? "Ask me anything — about a specific project, or to figure out what you're working on…"
                    : 'Ask about this project…'
            }
            rows={1}
            className="textarea textarea-bordered flex-1 bg-base-100 resize-none overflow-hidden text-sm"
          />
          <button
            className="btn btn-primary btn-sm"
            onClick={() => handleSend()}
            disabled={!input.trim() || isFileLoading}
          >
            <LuSend />
          </button>
        </div>
        <div className="text-xs opacity-40 mt-1">Enter to send · Shift+Enter for new line</div>
      </div>
    </div>
  );
};

export default SessionChatView;
